import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ChevronLeft, CheckCircle2, Loader2, AlertTriangle,
  ShieldCheck, Star,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { buildRoute, ROUTES } from '@/config/routes'
import useAuthStore from '@/stores/authStore'
import Button from '@/components/ui/Button'

export default function JobCompletion() {
  const { id: bookingId } = useParams()
  const navigate = useNavigate()
  const profile  = useAuthStore((s) => s.profile)

  const [loading,    setLoading]    = useState(true)
  const [booking,    setBooking]    = useState(null)
  const [afterPhotos, setAfterPhotos] = useState([])
  const [pageError,  setPageError]  = useState(null)
  const [accepting,  setAccepting]  = useState(false)
  const [accepted,   setAccepted]   = useState(false)
  const [checked,    setChecked]    = useState(false)

  useEffect(() => {
    if (!bookingId || !profile?.id) return

    async function load() {
      setLoading(true)

      const { data: bk, error } = await supabase
        .from('bookings')
        .select('id, ref_number, status, address, completed_at, warranty_expires, customer_id, worker_id')
        .eq('id', bookingId)
        .eq('customer_id', profile.id)
        .single()

      if (error || !bk) {
        setPageError('Booking not found.')
        setLoading(false)
        return
      }

      if (!['pending_approval', 'completed'].includes(bk.status)) {
        setPageError('This job is not yet marked as complete.')
        setLoading(false)
        return
      }

      // If already signed off (warranty_expires set), mark as accepted
      if (bk.warranty_expires) setAccepted(true)

      setBooking(bk)

      // Load after photos
      const { data: photos } = await supabase
        .from('booking_photos')
        .select('photo_path')
        .eq('booking_id', bookingId)
        .eq('photo_type', 'after')
        .order('created_at', { ascending: false })
        .limit(6)

      const urls = await Promise.all(
        (photos ?? []).map(async (row) => {
          const { data: signed } = await supabase.storage
            .from('job-photos')
            .createSignedUrl(row.photo_path, 3600)
          return signed?.signedUrl ?? null
        })
      )
      setAfterPhotos(urls.filter(Boolean))
      setLoading(false)
    }

    load()
  }, [bookingId, profile?.id]) // eslint-disable-line

  async function handleAccept() {
    if (!checked) return
    setAccepting(true)

    // Set warranty_expires = 1 year from today
    const warrantyDate = new Date()
    warrantyDate.setFullYear(warrantyDate.getFullYear() + 1)

    const { error } = await supabase
      .from('bookings')
      .update({
        warranty_expires: warrantyDate.toISOString(),
        status: 'completed', // ensure it's marked complete
      })
      .eq('id', bookingId)
      .eq('customer_id', profile.id)

    setAccepting(false)
    if (error) { console.error(error); return }

    setAccepted(true)
  }

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-surface-base">
        <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
      </div>
    )
  }

  if (pageError) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-surface-base gap-4 px-6 text-center">
        <AlertTriangle className="w-8 h-8 text-siglat-red" />
        <p className="text-ink-primary font-semibold">{pageError}</p>
        <button onClick={() => navigate(ROUTES.BOOK_HISTORY)} className="text-sm text-siglat-teal hover:underline">
          Back to My Projects
        </button>
      </div>
    )
  }

  // ── Accepted state ──────────────────────────────────────────────────────────
  if (accepted) {
    const warrantyDate = booking?.warranty_expires
      ? new Date(booking.warranty_expires).toLocaleDateString('en-PH', {
          year: 'numeric', month: 'long', day: 'numeric',
        })
      : null

    return (
      <div className="min-h-dvh bg-surface-base flex flex-col">
        <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top">
          <div className="page-container">
            <p className="text-sm text-ink-muted">Job Completion</p>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-5">
          <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/25 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
          <div>
            <p className="font-display text-xl font-bold text-ink-primary mb-2">Job Accepted!</p>
            <p className="text-sm text-ink-muted leading-relaxed">
              Thank you for confirming. Your job is now complete.
            </p>
          </div>

          {warrantyDate && (
            <div className="bg-surface-raised border border-siglat-teal/25 rounded-xl px-4 py-3 w-full max-w-xs flex items-start gap-3 text-left">
              <ShieldCheck className="w-5 h-5 text-siglat-teal shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-ink-primary">1-Year Warranty Active</p>
                <p className="text-xs text-ink-muted mt-0.5">Valid until {warrantyDate}</p>
              </div>
            </div>
          )}

          <Button
            fullWidth
            size="lg"
            onClick={() => navigate(buildRoute.bookReview(bookingId))}
          >
            <Star className="w-4 h-4" />
            Rate Your Worker
          </Button>
          <button
            onClick={() => navigate(ROUTES.BOOK_HISTORY)}
            className="text-sm text-ink-muted hover:text-ink-primary"
          >
            Skip — Back to My Projects
          </button>
        </div>
      </div>
    )
  }

  const completedAt = booking?.completed_at
    ? new Date(booking.completed_at).toLocaleDateString('en-PH', {
        month: 'long', day: 'numeric', year: 'numeric',
      })
    : null

  // ── Sign-off form ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-dvh bg-surface-base flex flex-col">

      {/* Header */}
      <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top shrink-0">
        <div className="page-container">
          <button
            onClick={() => navigate(ROUTES.BOOK_HISTORY)}
            className="flex items-center gap-1 text-sm text-ink-muted hover:text-ink-secondary mb-3 -ml-1"
          >
            <ChevronLeft className="w-4 h-4" />
            My Projects
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/25 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold text-ink-primary leading-tight">
                Job Completion
              </h1>
              <p className="text-xs text-ink-muted">
                {booking?.ref_number ?? bookingId.slice(0, 8).toUpperCase()}
                {completedAt ? ` · Completed ${completedAt}` : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="page-container py-5 flex flex-col gap-5">

          {/* Status message */}
          <div className="bg-green-500/10 border border-green-500/25 rounded-xl px-4 py-4 text-center">
            <p className="text-sm font-semibold text-green-400 mb-1">
              Your job has been marked as complete!
            </p>
            <p className="text-xs text-ink-muted leading-relaxed">
              Please review the after photos below and confirm you accept the completed work.
              Your 1-year warranty will activate once you sign off.
            </p>
          </div>

          {/* After photos */}
          {afterPhotos.length > 0 && (
            <div>
              <p className="text-sm font-medium text-ink-secondary mb-2">After Photos</p>
              <div className="grid grid-cols-3 gap-1.5">
                {afterPhotos.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={url}
                      alt={`After photo ${i + 1}`}
                      className="w-full aspect-square object-cover rounded-xl border border-surface-border"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Address */}
          {booking?.address && (
            <div className="bg-surface-raised border border-surface-border rounded-xl px-4 py-3">
              <p className="text-xs text-ink-muted uppercase tracking-widest mb-0.5">Location</p>
              <p className="text-sm text-ink-primary">{booking.address}</p>
            </div>
          )}

          {/* Warranty info */}
          <div className="bg-surface-raised border border-siglat-teal/20 rounded-xl px-4 py-3 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-siglat-teal shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-ink-primary">1-Year Workmanship Warranty</p>
              <p className="text-xs text-ink-muted mt-0.5 leading-relaxed">
                By accepting, you confirm the work meets your expectations. Siglat Konstract provides a 1-year warranty on all completed workmanship.
              </p>
            </div>
          </div>

          {/* Acceptance checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <div className="mt-0.5">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="w-4 h-4 accent-siglat-teal rounded"
              />
            </div>
            <p className="text-sm text-ink-secondary leading-relaxed">
              I confirm that the work has been completed to my satisfaction and I accept the completed job.
            </p>
          </label>

        </div>
      </div>

      {/* Bottom CTA */}
      <div className="shrink-0 bg-surface-raised border-t border-surface-border px-4 py-4 safe-bottom">
        <div className="page-container">
          <Button
            fullWidth
            size="lg"
            loading={accepting}
            disabled={!checked}
            onClick={handleAccept}
          >
            <CheckCircle2 className="w-4 h-4" />
            Accept &amp; Activate Warranty
          </Button>
        </div>
      </div>

    </div>
  )
}
